"use client"

import type { ReactNode } from "react"

import { Button, CountBadge } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"

interface BottomNavTabProps {
  label: string
  active: boolean
  outline: ReactNode
  filled: ReactNode
  href?: string
  onPress?: () => void
  expanded?: boolean
  badge?: number
}

const TAB =
  "hit-target focus-visible:ring-brand group/tab relative flex h-full min-w-0 flex-col items-center justify-start gap-0 rounded-none px-0 pt-275 font-normal focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"

export const BottomNavTab = ({ label, active, outline, filled, href, onPress, expanded, badge }: BottomNavTabProps) => {
  const tone = active ? "text-fg-brand" : "text-fg-heading"
  const body = (
    <>
      <span className="press-glyph relative grid h-5 w-6 items-center justify-items-center group-active/tab:scale-88">
        <span aria-hidden className={`glyph-swap absolute ${active ? "scale-104 opacity-0" : "scale-100 opacity-100"}`}>
          {outline}
        </span>
        <span aria-hidden className={`glyph-swap absolute ${active ? "scale-100 opacity-100" : "scale-94 opacity-0"}`}>
          {filled}
        </span>
        {badge !== undefined && <CountBadge count={badge} size="compact" className="absolute -top-150 -right-300" />}
      </span>
      <span className={`font-text text-tab mt-100 ${tone}`}>{label}</span>
    </>
  )

  if (href === undefined) {
    return (
      <Button variant="quiet" fullWidth onClick={onPress} aria-expanded={expanded} aria-current={active && !expanded ? "page" : undefined} className={`${TAB} ${tone}`}>
        {body}
      </Button>
    )
  }

  return (
    <LocalizedClientLink href={href} aria-current={active ? "page" : undefined} className={`${TAB} flex ${tone}`}>
      {body}
    </LocalizedClientLink>
  )
}
